import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import {
  Box,
  Flex,
  Heading,
  IconOnePass,
  Link,
  LinkProps,
} from "@catchoftheday/cg-components";
import { styled, themeGet, useTheme } from "@catchoftheday/theme";
import { isTouchDevice, minusOnePx, useMedia } from "@catchoftheday/utilities";

import { Shop } from "@header/types";

import { ItemPath, trackMenuClick } from "../../utilities/analytics";

const FIRST_MENU_ITEM_INDEX = 1;

const Popover = styled(Flex)`
  flex-shrink: 0;
  height: 100%;
  align-items: center;
  @media (max-width: ${(props) =>
      minusOnePx(themeGet("breakpoints.lg")(props))}) and (min-width: ${(
      props
    ) => themeGet("breakpoints.md")(props)}) {
    height: auto;
  }
`;

type OnePassNavItem = LinkProps & {
  isActive: boolean;
};

const OnePassNavItem = styled(Link)<OnePassNavItem>`
  display: flex;
  align-items: center;
  position: relative;
  margin-right: 20px;
  outline: none;

  &:hover,
  &:focus,
  &:active {
    color: ${(props) => themeGet("colors.onePass.primary")(props)};
    text-decoration: none;
    outline: none;
  }

  @media (min-width: ${themeGet("breakpoints.lg")}) {
    height: 100%;
    &:hover,
    &:active {
      .hover-marker {
        visibility: visible;
        opacity: 1;
      }
    }
  }
  @media (max-width: ${(props) =>
      minusOnePx(themeGet("breakpoints.lg")(props))}) and (min-width: ${(
      props
    ) => themeGet("breakpoints.md")(props)}) {
    height: auto;
    border: solid 1px;
    border-color: ${(props) =>
      themeGet(
        props.isActive ? "colors.onePass.primary" : "colors.ui.borderColor"
      )(props)};
    border-radius: 16px;
    padding: 3px 12px;
    margin: 0px 4px;

    &:active {
      box-shadow: inset 0px 4px 4px ${(props) => themeGet("colors.grey")(props)};
    }
  }
`;

type PrimaryNavItemProps = LinkProps & {
  isClearance: boolean;
  isActive: boolean;
};

const PrimaryNavItem = styled(Link)<PrimaryNavItemProps>`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  font-size: ${(props) => themeGet("fontSizes.sm")(props)};
  padding: 4px;
  margin-right: 20px;
  color: ${(props) =>
    props.isClearance ? "#FF5061" : themeGet("colors.text")(props)};

  &:hover,
  &:focus,
  &:active {
    color: ${(props) =>
      props.isClearance ? "#FF5061" : themeGet("colors.text")(props)};
    text-decoration: none;
    outline: none;
  }

  @media (min-width: ${themeGet("breakpoints.lg")}) {
    &:hover,
    &:focus,
    &:active {
      .hover-marker {
        visibility: visible;
        opacity: 1;
      }
    }
  }
  @media (max-width: ${(props) =>
      minusOnePx(
        themeGet("breakpoints.lg")(props)
      )}) and (min-width: ${themeGet("breakpoints.md")}) {
    height: auto;
    border: solid 1px
      ${(props) =>
        themeGet(
          props.isActive
            ? props.isClearance
              ? "#FF5061"
              : "colors.text"
            : "colors.ui.borderColor"
        )(props)};
    border-radius: 100px;
    padding: 8px 16px;
    margin: 0px 4px;

    &:active {
      box-shadow: inset 0px 4px 4px ${(props) => themeGet("colors.grey")(props)};
    }
  }
`;

const HoverMarker = styled(Box)`
  position: absolute;
  height: 2px;
  width: 100%;
  bottom: 0px;
  z-index: 1;
  left: 0px;
  transition: all ease 100ms;

  @media (max-width: ${(props) =>
      minusOnePx(
        themeGet("breakpoints.lg")(props)
      )}) and (min-width: ${themeGet("breakpoints.md")}) {
    display: none;
  }
`;

type MenuItemProps = {
  index: number;
  tabIndex: number;
  shop: Shop;
  itemPathShop: ItemPath[];
  touchedItem: number | undefined;
  setTouchedItem: Dispatch<SetStateAction<number | undefined>>;
  recordScrollPosition: () => void;
};

export const MenuItem: React.FC<MenuItemProps> = ({
  index,
  tabIndex,
  shop,
  itemPathShop,
  touchedItem,
  setTouchedItem,
  recordScrollPosition,
}) => {
  const theme = useTheme();
  const isDesktop = useMedia<boolean>(
    [`(min-width: ${theme.breakpoints.lg})`],
    [true]
  );
  const [isActive, setIsActive] = useState(false);
  const isOnePass = shop.slug === "onepass";
  const isTodaysDeals = shop.slug === "today-s-deals";
  const isClearance = shop.slug === "clearance";
  const hasSubmenu = shop.shopNavigationGroups.length > 0;

  const onMenuClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    if (isTouchDevice() && isDesktop && hasSubmenu && touchedItem !== shop.id) {
      event.preventDefault();
      setTouchedItem(shop.id);
      return;
    }
    trackMenuClick({
      name: shop.name,
      url: `${location.origin}${shop.url}/`,
      item_path: itemPathShop,
    });
    recordScrollPosition();
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsActive(
        window.location.pathname.replace(/\/$/, "") === shop.url ||
          (isTodaysDeals && window.location.pathname === "/")
      );
    }
    return () => setIsActive(false);
  }, [isTodaysDeals, shop.url]);

  return (
    <Popover ml={index == FIRST_MENU_ITEM_INDEX ? "3px" : 0}>
      <>
        {isOnePass ? (
          <OnePassNavItem
            id={`menu-bar-item-${index}`}
            tabIndex={tabIndex}
            href={shop.url}
            aria-current={isActive ? "page" : "false"}
            isActive={isActive}
            color="onePass.primary"
            hoverColor="onePass.primary"
            role="menuitem"
            onClick={onMenuClick}
          >
            <IconOnePass key={shop.id} width="72px" title="OnePass Shop" />
            <HoverMarker
              className="hover-marker"
              visibility={isActive ? "visible" : "hidden"}
              opacity={isActive ? "1" : "0"}
              bg="onePass.primary"
            />
          </OnePassNavItem>
        ) : (
          <PrimaryNavItem
            id={`menu-bar-item-${index}`}
            tabIndex={tabIndex}
            className="primary-nav"
            role="menuitem"
            aria-haspopup
            aria-current={isActive ? "page" : false}
            isActive={isActive}
            href={shop.url}
            onClick={onMenuClick}
            isClearance={isClearance}
          >
            <Heading.h5
              key={shop.id}
              color="inherit"
              m="0"
              fontWeight={{ md: "normal", lg: "light" }}
              lineHeight="1"
            >
              {shop.name}
            </Heading.h5>
            <HoverMarker
              className="hover-marker"
              visibility={isActive ? "visible" : "hidden"}
              opacity={isActive ? "1" : "0"}
              bg={isClearance ? "#FF5061" : "textTertiary"}
            />
          </PrimaryNavItem>
        )}
      </>
    </Popover>
  );
};
